<template>
  <div>
    <div
      v-if="activePitchType"
      ref="pitchtypeDetail"
      :style="{
        minHeight: pitchtypeDetailHeight ? `${pitchtypeDetailHeight}px` : null,
      }"
    >
      <lazy-campsite-pitchtype-detail-main
        :campsite-opening-dates-range="campsite.openingDateRanges"
        :category-ids="categoryIds"
        :campsite="campsite"
        :date-range="dateRange"
        :pitchtype="activePitchType"
        :party="party"
        :charge-types-updating="chargeTypesUpdating"
        :has-availability="hasAvailability"
        @range-changed="$emit('rangeChanged', $event)"
      />
    </div>

    <template v-if="!$isDesktop">
      <section
        class="cs-photos"
        :class="{ 'no-primary': !campsite.primaryPhoto }"
      >
        <new-campsite-badge
          v-if="campsite.firstAvailableDate"
          :first-available-date="campsite.firstAvailableDate"
        />

        <client-only>
          <campsite-favourite-button
            :campsite-id="campsite.id"
            :campsite-name="campsite.name"
            class="cs-favourite"
          />
        </client-only>
        <AsyncCampsitePhotosProvider
          :search-result-index="0"
          :campsite-id="campsite.id"
          :campsite-name="campsite.name"
          :category-ids="types"
          :gallery-full-screen="false"
          :primary-photo="primaryPhoto"
          :show-search-gallery="false"
          :gallery-initial-index="galleryInitialIndex"
          :photos-prop="seoPhotos"
          :photo-count="undefined"
          :load-photos-on-mount="true"
          @click="navigateToAlbums"
        />
      </section>
      <AsyncCampsiteDetailCardContent
        v-bind="cardContentProps"
        @scroll-triggered="scrolledToElement = $event"
      />
    </template>
    <template v-else>
      <AsyncCampsiteDetailHeroDesktop
        v-bind="cardContentProps"
        :types="types"
        :primary-photo="primaryPhoto"
        :search-filters="searchFilters"
      />

      <div
        v-observe-visibility="{
          callback: headerVisibilityChanged,
          throttle: 350,
          throttleOptions: { leading: 'both' },
        }"
        data-header-visibility-watcher
      />
      <CampsitePageNavigation
        v-if="!activePitchType"
        :campsite="campsite"
        :category-ids="categoryIds"
        :date-range="dateRange"
        :is-dated="isDated"
        :navigation-offscreen="navigationOffscreen"
        :party="party"
        @scroll-triggered="scrolledToElement = $event"
      />

      <AsyncCampsiteDescriptionDesktop
        v-if="!activePitchType"
        :campsite="campsite"
        :open="
          scrolledToElement === '#description' || $route.hash === '#description'
        "
        @subsection-closed="scrolledToElement = ''"
      />
    </template>

    <div v-show="nearbyPosition === 'top' && !activePitchType">
      <translate
        tag="h4"
        class="no-availability-message"
        data-no-availability-scroll-target
      >
        No availability here for your search criteria
      </translate>

      <div id="nearby-top-target"></div>

      <AsyncNotifyMe
        v-if="shouldShowNotifyMe"
        :key="getAsyncComponentKey('notifyMe')"
        :campsite-name="campsite.name"
        :pitchtypes="notifiablePitchtypes"
        :date-range="dateRange"
        :party="party"
        @vue:mounted="setAsyncComponentKey('notifyMe')"
      />
    </div>

    <div
      ref="pitchtypes"
      :style="{ minHeight: pitchtypesHeight ? `${pitchtypesHeight}px` : null }"
      class="pitch-types-group-wrapper"
      :class="{ desktop: $isDesktop }"
    >
      <pitch-types-group
        v-if="!activePitchType"
        :available-to-search="campsite.availableToSearch"
        :campsite-id="campsite.id.toString()"
        :campsite-categories="campsite.categories"
        :campsite-opening-date-ranges="campsite.openingDateRanges"
        :campsite-pitch-category-ids="campsite.pitchCategoryIds"
        :selected-facets="facets"
        :campsite-name="campsite.name"
        :campsite-slug="campsite.slug"
        :campsite-hierarchy-path="campsite.path"
        :campsite-super-facilities="campsite.superFacilities"
        :campsite-allow-override-immediate="
          campsite.paymentInfo.allowOverrideImmediate
        "
        :campsite-payment-timing="campsite.paymentTiming"
        :campsite-refund-policy="campsite.refundPolicy"
        :campsite-useful-info="campsite.usefulInfo"
        :campsite-terms-useful-info="campsite.termsUsefulInfo"
        :campsite-min-adult-age="campsite.minAdultAge"
        :campsite-adults-only="campsite.adultsOnly"
        :charge-types-updating="chargeTypesUpdating"
        :has-availability="hasAvailability"
        :pitchtypes="sortedPitchtypes"
        :party="party"
        :date-range="dateRange"
        :selected-types="types"
        :current-pitchtype-id="pitchtypeId"
        :calendar-start-date="campsite.calendarStartDate"
        @pitch-type-section-mounted="onPitchTypesGroupsMounted"
      />
    </div>

    <AsyncCampsiteReviewsSubsection
      v-if="$isDesktop"
      :key="getAsyncComponentKey('campsiteReviewsSubsectionDesktop')"
      :campsite-name="campsite.name"
      :campsite-slug="campsite.slug"
      :campsite-rate-count="campsite.rateCount"
      :campsite-rating="parseFloat(campsite.rating10.toFixed(1))"
      :review-id="reviewId"
      @data-fetched.once="onReviewsDataFetched"
      @vue:mounted="setAsyncComponentKey('campsiteReviewsSubsectionDesktop')"
    />

    <!-- <async-component-skeleton skeleton-height="50em"> -->
    <AsyncSubsectionsGroup
      :key="getAsyncComponentKey('subsectionsGroup')"
      :campsite="campsite"
      :scrolled-to-element="scrolledToElement"
      @subsection-closed="scrolledToElement = ''"
      @vue:mounted="setAsyncComponentKey('subsectionsGroup')"
    />
    <!-- </async-component-skeleton> -->

    <div
      v-if="!isDated && campsite.availableToSearch && !$isDesktop"
      class="additional-check-availability"
    >
      <AsyncCampsiteAvailabilityLink
        v-if="!isDated"
        :key="getAsyncComponentKey('campsiteAvailabilityLink')"
        :hierarchy-path="campsite.path"
        :slug="campsite.slug"
        class="button primary asblock check-availability-btn"
        @vue:mounted="setAsyncComponentKey('campsiteAvailabilityLink')"
      >
        <translate> Check availability </translate>
      </AsyncCampsiteAvailabilityLink>
    </div>

    <template v-if="!$isDesktop">
      <AsyncCampsiteReviewsSubsection
        :key="getAsyncComponentKey('campsiteReviewsSubsectionMobile')"
        :campsite-name="campsite.name"
        :campsite-slug="campsite.slug"
        :campsite-rate-count="campsite.rateCount"
        :campsite-rating="parseFloat(campsite.rating10.toFixed(1))"
        :review-id="reviewId"
        @data-fetched.once="onReviewsDataFetched"
        @vue:mounted="setAsyncComponentKey('campsiteReviewsSubsectionMobile')"
      />
    </template>

    <div id="nearby-bottom-target"></div>

    <AsyncCampsiteDetailsPromoPages
      :key="getAsyncComponentKey('campsiteDetailsPromoPages')"
      :slug="campsite.slug"
      class="campsite-subsection"
      @vue:mounted="setAsyncComponentKey('campsiteDetailsPromoPages')"
    />

    <!-- Single nearby component that teleports to correct position -->
    <client-only>
      <Teleport :to="nearbyTeleportTarget">
        <AsyncNearbyBookable
          v-if="!activePitchType"
          :key="getAsyncComponentKey('nearbyBookable')"
          v-bind="nearbyBookableProps"
          :class="`${nearbyPosition}-position`"
          @vue:mounted="setAsyncComponentKey('nearbyBookable')"
        />
      </Teleport>
    </client-only>

    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import { debounce } from 'lodash-es'
import { defineAsyncComponent, hydrateOnIdle, toRaw } from 'vue'
import { arrayByKey } from '~/utility/array'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import type { JumpOptions } from '../../../plugins/scrollto'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { logger } from '../../../utility/logger'
import { PitchTypeWithChargeType } from '../../campsites/types'
import type { SearchFilters } from '../../search/schemas'
import type {
  CampsiteDetails,
  CampsiteDetailsBreadcrumbs,
  ChargeType,
  PitchType,
  SchemaImageObject,
} from '../types'
import {
  getAvailablePitchtypesMatchingTypes,
  getPitchtypesMatchingTypes,
} from '../utils'
import { CampsiteDetailBookableListingProps } from './CampsiteDetailBookableListingProps'
import { CampsiteDetailCardContentProps } from './CampsiteDetailCardContentProps'
import CampsitePageNavigation from './CampsitePageNavigation.vue'
import { getPrimaryPhotoFromPitchType } from './getPrimaryPhoto'
import type { Party } from './types'

const AsyncSubsectionsGroup = defineAsyncComponent({
  loader: () => import('../../campsites/components/SubsectionsGroup.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncNearbyBookable = defineAsyncComponent({
  loader: () => import('../nearby/components/NearbyBookable.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsitePhotosProvider = defineAsyncComponent({
  loader: () =>
    import(
      '../../../components/campsite/campsite-card/PuCampsitePhotosProvider.vue'
    ),
  hydrate: hydrateOnIdle(10000),
})

const AsyncCampsiteDetailCardContent = defineAsyncComponent({
  loader: () => import('./CampsiteDetailCardContent.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsiteDetailHeroDesktop = defineAsyncComponent({
  loader: () => import('./CampsiteDetailHeroDesktop.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsiteDescriptionDesktop = defineAsyncComponent({
  loader: () => import('./CampsiteDescriptionDesktop.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncNotifyMe = defineAsyncComponent({
  loader: () => import('../../allocation-alert/components/NotifyMe.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteReviewsSubsection = defineAsyncComponent({
  loader: () =>
    import(
      '../../campsites/components/subsections/CampsiteReviewsSubsection.vue'
    ),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteAvailabilityLink = defineAsyncComponent({
  loader: () =>
    import('../../pu-links/link-campsite/PuCampsiteAvailabilityLink.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteDetailsPromoPages = defineAsyncComponent({
  loader: () => import('../promo-pages/CampsiteDetailsPromoPages.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  components: {
    AsyncSubsectionsGroup,
    AsyncNearbyBookable,
    AsyncCampsitePhotosProvider,
    AsyncCampsiteDetailCardContent,
    AsyncCampsiteDetailHeroDesktop,
    AsyncCampsiteDescriptionDesktop,
    AsyncNotifyMe,
    AsyncCampsiteReviewsSubsection,
    AsyncCampsiteAvailabilityLink,
    AsyncCampsiteDetailsPromoPages,
    CampsitePageNavigation,
  },
  emits: ['pts-viewed', 'rangeChanged'],
})
class CampsiteDetailBookableListing
  extends Vue
  implements CampsiteDetailBookableListingProps
{
  @Prop({ required: true })
  campsite: CampsiteDetails

  @Prop({ required: true })
  party: Party

  @Prop({ required: true })
  types: number[]

  @Prop({ required: true })
  facets: string[]

  @Prop({ required: true })
  dateRange: { start: string; end: string }

  @Prop({ required: true })
  chargeTypesUpdating: boolean

  @Prop({ required: true })
  chargetypes: ChargeType[]

  @Prop({ required: true })
  isDated: boolean

  @Prop({ required: true })
  hasAvailability: boolean | null

  @Prop({ required: true })
  pitchtypeId: string

  @Prop({ required: false })
  scrollToPitchtypeId: string | null

  @Prop({ required: true })
  searchFilters: SearchFilters

  @Prop({ required: true })
  categoryIds: string[]

  @Prop()
  previousPageWasCheckAvailability: boolean

  @Prop()
  breadcrumbs: CampsiteDetailsBreadcrumbs[]

  @Prop()
  reviewId?: string

  pitchtypesHeight: number | null = null
  pitchtypeDetailHeight: number | null = null
  private pitchtypeDetailResizeObserver: ResizeObserver | null = null

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  scrolledToElement = ''
  isMounted = false
  galleryInitialIndex = 0
  imagesToLoadBeforeHydration = 8
  mountedScrollToSelector: string | null = null
  pitchtypesSectionObserver: IntersectionObserver
  pitchtypesResizeObserver: ResizeObserver | null = null
  hasScrolledToReview = false

  mounted() {
    const cssgal = document.getElementById('search-gallery-0')
    if (cssgal && cssgal.dataset.galleryPosition) {
      const leftPos = parseInt(cssgal.dataset.galleryPosition)

      if (cssgal && leftPos) {
        const galleryImages = cssgal.children
        for (let i = 0; i < galleryImages.length; i++) {
          if (
            leftPos + 50 > (galleryImages[i] as HTMLImageElement).offsetLeft &&
            leftPos - 50 < (galleryImages[i] as HTMLImageElement).offsetLeft
          ) {
            this.galleryInitialIndex = i
            break
          }
        }
      }
    }

    // Track pitch types section height asynchronously via ResizeObserver
    // instead of synchronous offsetHeight reads (which caused 928ms forced layout)
    this.setupPitchtypesResizeObserver()

    // Fire-and-forget: intentionally not awaiting this callback
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- requestIdleCallback accepts async function
    void requestIdleCallback(async () => {
      if (!this.$.isUnmounted) {
        await this.$nextTick(() => {
          if (!this.$.isUnmounted) {
            this.isMounted = true
          }
        })
      }
    })

    this.scrollToReviewIfNeeded()
  }

  beforeUnmount() {
    // Clean up observers
    if (this.pitchtypesSectionObserver) {
      this.pitchtypesSectionObserver.disconnect()
    }
    if (this.pitchtypesResizeObserver) {
      this.pitchtypesResizeObserver.disconnect()
      this.pitchtypesResizeObserver = null
    }
    if (this.pitchtypeDetailResizeObserver) {
      this.pitchtypeDetailResizeObserver.disconnect()
      this.pitchtypeDetailResizeObserver = null
    }
    // Cancel any pending debounced calls
    this.debounceCorrectAvailabilityScroll.cancel()
  }

  scrollToReviewIfNeeded() {
    if (this.reviewId && !this.hasScrolledToReview && !this.$.isUnmounted) {
      this.$puScrollTo(`#reviews_${this.reviewId}`, {
        duration: 200,
        offset: this.$isDesktop ? -40 : 0,
      })
      if (
        process.client &&
        document.querySelector(`#reviews_${this.reviewId}`)
      ) {
        this.hasScrolledToReview = true
      }
    }
  }

  onReviewsDataFetched() {
    if (this.$.isUnmounted) return
    this.scrollToReviewIfNeeded()
  }

  async onPitchTypesGroupsMounted() {
    if (this.$.isUnmounted) return

    if (this.mountedScrollToSelector && !this.$.isUnmounted) {
      this.$puScrollTo(this.mountedScrollToSelector, { duration: 200 })
      this.mountedScrollToSelector = null
    } else {
      await new Promise((resolve) => setTimeout(resolve, 300))
      if (!this.$.isUnmounted) {
        this.correctPitchtypesOffset()
      }
    }
  }

  correctPitchtypesOffset() {
    if (
      !process.client ||
      !('IntersectionObserver' in window) ||
      this.$.isUnmounted
    )
      return
    const selector = '[data-pitchtypes-scroll-target]'
    const element = document.querySelector(selector)
    if (!element || this.$.isUnmounted) return

    // Clean up existing observer
    if (this.pitchtypesSectionObserver) {
      this.pitchtypesSectionObserver.disconnect()
    }

    this.pitchtypesSectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (
        entry.isIntersecting &&
        entry.intersectionRect.top === 0 &&
        !this.$.isUnmounted
      ) {
        this.$puScrollTo(selector, { duration: 200 })
      }
      if (this.pitchtypesSectionObserver) {
        this.pitchtypesSectionObserver.disconnect()
      }
    })
    this.pitchtypesSectionObserver.observe(element)
  }

  get head() {
    logger('CampsiteDetailBookableListing head')
    return {
      script: this.imageObjects,
    }
  }

  get seoPhotos() {
    return this.campsite.seoPhotos
      ?.map((photo) => {
        const url = { masterImage: photo.url }
        return {
          ...photo,
          url,
        }
      })
      .slice(0, 10)
  }

  get imageObjects() {
    const scriptData: SchemaImageObject[] = []
    const numberOfPitchtypes = this.campsite.pitchtypes.length || 0
    for (let i = 0; i < numberOfPitchtypes; i++) {
      scriptData.push(this.schemaImageObject(i))
    }
    return scriptData
  }

  setupPitchtypesResizeObserver() {
    const element = this.$refs.pitchtypes as HTMLElement | undefined
    if (!element) return

    this.pitchtypesResizeObserver = new ResizeObserver((entries) => {
      if (this.activePitchType || this.$.isUnmounted) return
      const entry = entries[0]
      if (entry) {
        // borderBoxSize is border-box; contentRect.height is content-box.
        // Both are equivalent here since the element has no padding/border.
        const height = Math.round(
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height,
        )
        if (height > 0) {
          this.pitchtypesHeight = height
        }
      }
    })
    this.pitchtypesResizeObserver.observe(element, { box: 'border-box' })
  }

  setupPitchtypeDetailResizeObserver() {
    const element = this.$refs.pitchtypeDetail as HTMLElement | undefined
    if (!element) return

    // Reuse existing observer if already created
    if (this.pitchtypeDetailResizeObserver) {
      this.pitchtypeDetailResizeObserver.disconnect()
    } else {
      this.pitchtypeDetailResizeObserver = new ResizeObserver((entries) => {
        if (!this.activePitchType || this.$.isUnmounted) return
        // Freeze height during charge types reload: the content temporarily
        // collapses when old data is cleared, which would lower min-height
        // and cause CLS. Only update when content is stable.
        if (this.chargeTypesUpdating) return
        const entry = entries[0]
        if (entry) {
          const height = Math.round(
            entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height,
          )
          if (height > 0) {
            this.pitchtypeDetailHeight = height
          }
        }
      })
    }
    this.pitchtypeDetailResizeObserver.observe(element, { box: 'border-box' })
  }

  schemaImageObject(index: number): SchemaImageObject {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      name: this.campsite.name,
      contentLocation: `${this.campsite.name}, ${this.campsite.hierarchyTextShort}`,
    }
    if (this.photoUrl(index)) {
      json.contentUrl = this.photoUrl(index)
    }
    if (this.$i18n.locale.split('-')[0] === 'en') {
      json.description = this.campsite.pitchtypes[index]?.photos[0]?.caption
    }
    return {
      type: 'application/ld+json',
      body: true,
      json,
    }
  }

  photoUrl(index: number) {
    return this.campsite.pitchtypes[index]?.photos[0]?.url.galleryLarge ?? null
  }

  @Watch('nearbyPosition', { immediate: true })
  nearbyChanged() {
    if (
      this.hasAvailability === null ||
      !this.previousPageWasCheckAvailability
    ) {
      return
    }

    const { nearbyPosition, activePitchType } = this

    if (nearbyPosition && !activePitchType) {
      this.scrollToWhenReady(
        nearbyPosition === 'top'
          ? '[data-no-availability-scroll-target]'
          : '[data-campsite-subsection]',
      )
    }
  }

  scrollToWhenReady(selector: string, options?: JumpOptions) {
    if (!process.client || !document.querySelector(selector)) {
      this.mountedScrollToSelector = selector
      return
    }
    this.mountedScrollToSelector = null
    const defaultScrollToOptions: JumpOptions = { duration: 200 }
    const scrollToOptions: JumpOptions = options
      ? { ...defaultScrollToOptions, ...options }
      : defaultScrollToOptions
    this.$puScrollTo(selector, scrollToOptions)
  }

  // TODO: computed prop?
  activePitchType: PitchType | PitchTypeWithChargeType | null = null

  ptsViewed: string[] = []

  @Watch('activePitchType')
  onActivePitchTypeChangeForObserver() {
    // Set up / tear down the detail view ResizeObserver when entering/leaving
    // pitch type detail view. Prevents CLS during charge types reload by
    // preserving the container's min-height.
    if (this.activePitchType) {
      void this.$nextTick(() => {
        this.setupPitchtypeDetailResizeObserver()
      })
    } else {
      if (this.pitchtypeDetailResizeObserver) {
        this.pitchtypeDetailResizeObserver.disconnect()
        this.pitchtypeDetailResizeObserver = null
      }
      this.pitchtypeDetailHeight = null
    }
  }

  @Watch('dateRange')
  @Watch('sortedPitchtypes')
  @Watch('pitchtypeId', { immediate: true })
  onNeedToUpdateActivePitchtype() {
    if (this.$?.isUnmounted) return

    if (this.pitchtypeId) {
      if (!this.ptsViewed.includes(this.pitchtypeId)) {
        this.ptsViewed.push(this.pitchtypeId)
        this.$emit('pts-viewed', this.ptsViewed.length)
      }
    }

    this.activePitchType = this.pitchtypeId
      ? this.sortedPitchtypes.find(({ id }) => id === this.pitchtypeId) ||
        this.activePitchType
      : null
  }

  @Watch('sortedPitchtypes')
  onSortedPitchtypesChange() {
    this.debounceCorrectAvailabilityScroll()
  }

  private debounceCorrectAvailabilityScroll = debounce(
    this.correctAvailabilityScroll,
    500,
  )

  correctAvailabilityScroll() {
    if (this.$.isUnmounted) return

    const { nearbyPosition, hasAvailability } = this

    if (nearbyPosition && hasAvailability === false) {
      this.scrollToWhenReady(
        nearbyPosition === 'top'
          ? '[data-no-availability-scroll-target]'
          : '[data-campsite-subsection]',
        {
          offset: this.$isDesktop ? -70 : 0,
        },
      )
    }
  }

  @Watch('pitchtypeId', { immediate: true })
  onPitchtypeIdChange(value: string) {
    if (value) {
      const gtm = this.$gtm
      if (!gtm) return

      try {
        // Always clear previous ecommerce data (GTM best practice)
        gtm.push({ ecommerce: null })

        // Find the pitchtype directly from sortedPitchtypes (more reliable than activePitchType due to timing)
        const pitchtype = this.sortedPitchtypes.find(({ id }) => id === value)

        // Enhanced event with ecommerce data if available and non-empty
        if (
          pitchtype &&
          'chargetype' in pitchtype &&
          Array.isArray(pitchtype.chargetype?.ecommerce?.items) &&
          pitchtype.chargetype.ecommerce.items.length > 0
        ) {
          // Convert reactive data to raw plain objects for GTM
          const rawEcommerceItems = toRaw(pitchtype.chargetype.ecommerce.items)

          const gtmEvent = {
            event: 'Pitchtype Detail Opened',
            ecommerce: {
              items: rawEcommerceItems,
            },
          }

          gtm.push(gtmEvent)
        } else {
          // Fallback to original event without ecommerce data
          gtm.push({ event: 'Pitchtype Detail Opened' })
        }
      } catch {
        // Silently fail GTM tracking - don't break the UI
        // Still try to send the basic event without ecommerce
        try {
          gtm.push({ event: 'Pitchtype Detail Opened' })
        } catch {}
      }
    }
  }

  activated() {
    this.ptsViewed.length = 0
    this.$emit('pts-viewed', this.ptsViewed.length)
  }

  get hasChargeTypes() {
    return this.chargetypes?.length > 0
  }

  get cardContentProps(): CampsiteDetailCardContentProps {
    return {
      campsite: this.campsite,
      isDated: this.isDated,
      hasAvailability: this.hasAvailability || false,
    }
  }

  get sortedPitchtypes(): Array<PitchType | PitchTypeWithChargeType> {
    const sorted = this.isDated
      ? this.pitchtypesSortedByChargeTypesOrder
      : this.pitchtypesSortedByLeadPrice

    if (this.scrollToPitchtypeId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy method returns any
      return this.moveSelectedPitchtypeIdToStart(
        this.scrollToPitchtypeId,
        sorted,
      )
    }

    return sorted
  }

  // TODO: Add proper types for parameters instead of implicit any
  moveSelectedPitchtypeIdToStart(id: any, pitchtypes: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const pitchtype = pitchtypes.find((pt) => pt.id === id)
    if (pitchtype) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      return [pitchtype, ...pitchtypes.filter((pt) => pt.id !== pitchtype.id)]
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return pitchtypes
    }
  }

  get pitchtypesSortedByLeadPrice(): PitchType[] {
    if (!this.campsite) {
      return []
    }
    return this.campsite.pitchtypes.sort(
      (a, b) =>
        (a.leadPrice?.amount || Number.MAX_VALUE) -
        (b.leadPrice?.amount || Number.MAX_VALUE),
    )
  }

  get pitchtypesSortedByChargeTypesOrder(): PitchTypeWithChargeType[] {
    if (!this.campsite) {
      return []
    }
    const pitchtypes = arrayByKey(this.campsite.pitchtypes, 'id')
    const chargetypes = this.chargetypes.filter(
      (c) => !!pitchtypes[c.pitchtypeId],
    )
    const sortedPitchtypes = chargetypes.map((chargetype) => ({
      ...pitchtypes[chargetype.pitchtypeId],
      chargetype,
    }))
    return sortedPitchtypes || []
  }

  get notifiablePitchtypes() {
    const pitchtypes = this.pitchtypesSortedByChargeTypesOrder.filter(
      (p) => p.chargetype.isNotifiable,
    )
    return getPitchtypesMatchingTypes(pitchtypes, this.types, this.facets)
  }

  get nearbyBookableProps() {
    return {
      campsiteSlug: this.campsite.slug,
      hierarchyPath: this.campsite.path,
      name: this.campsite.name,
      point: this.campsite.point,
      adminAreaHierarchy: this.campsite.hierarchyBreadcrumbs[1],
      countryHierarchy: this.campsite.hierarchyBreadcrumbs[0],
    }
  }

  get nearbyPosition() {
    if (!this.hasChargeTypes && this.isDated) {
      return undefined
    }
    if (this.isDated && this.types?.length) {
      return getAvailablePitchtypesMatchingTypes(
        this.sortedPitchtypes as PitchTypeWithChargeType[],
        this.types,
      ).length
        ? 'bottom'
        : 'top'
    }
    return this.isDated && !this.hasAvailability ? 'top' : 'bottom'
  }

  get nearbyTeleportTarget() {
    return this.nearbyPosition === 'top'
      ? '#nearby-top-target'
      : '#nearby-bottom-target'
  }

  get pitchtypesWithCapacityAvailable() {
    const pitchtypes = getPitchtypesMatchingTypes(
      this.sortedPitchtypes,
      this.types,
      this.facets,
    )
    const overCapacityPitchtypes = pitchtypes.filter(
      (p) =>
        (p as PitchTypeWithChargeType).chargetype?.state === 'max_capacity',
    )
    return pitchtypes.length > overCapacityPitchtypes.length
  }

  get primaryPhoto() {
    return getPrimaryPhotoFromPitchType(
      this.campsite,
      this.searchFilters.categoryIds || [],
    )
  }

  get shouldShowNotifyMe() {
    return (
      this.pitchtypesWithCapacityAvailable && this.notifiablePitchtypes.length
    )
  }

  navigationOffscreen = false
  headerVisibilityChanged(isVisible) {
    if (this.$.isUnmounted) return
    this.navigationOffscreen = !isVisible
  }

  navigateToAlbums() {
    void this.$router.push({
      name: 'campsite-albums-album',
      params: this.$route.params,
      query: this.$route.query,
    })
  }
}

export default toNative(CampsiteDetailBookableListing)
</script>
<style scoped>
.additional-check-availability {
  padding: 1.4em 1em 2.1em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 4px solid var(--colour-page-background);
}

.cs-photos {
  height: 75vw;
  overflow: hidden;
}

.cs-photos.no-primary {
  height: auto;
}

.no-availability-message {
  background: black;
  color: white;
  line-height: 1.2;
  margin: 0;
  padding: 0.75em 1em;
}

.check-availability-btn {
  font-weight: 400;
  font-size: 1.5em;
  box-shadow: 0 0.275em var(--colour-dark-green);
}

.pitch-types-group-wrapper {
  background: var(--colour-page-background);
}

.pitch-types-group-wrapper.desktop {
  background: var(--colour-page-background-light);
}
</style>
